/** `/imports` — what has been imported, newest first. */
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { Surface } from "@packagepage/ui";

import { problemOf } from "../api/client.js";
import { api } from "../api/procedures.js";
import { STATUS_WORDS, count, readStats } from "./import-wizard.js";

import "@packagepage/ui/surface.css";
import "@packagepage/ui/button.css";
import "./import.css";

export function ImportHistory(): ReactNode {
  const history = useQuery({
    queryKey: ["imports", "list"],
    queryFn: () => api.imports(),
  });

  if (history.isPending) return <p data-screen-label="Imports loading">Loading…</p>;
  if (history.error !== null) {
    return (
      <Surface tone="warn" emphasis="filled">
        <p data-screen-label="Imports">
          {problemOf(history.error)?.detail ??
            "We could not load your imports. Reload to try again."}
        </p>
      </Surface>
    );
  }

  const imports = history.data.imports;
  return (
    <div className="pp-import" data-screen-label="Import history">
      <header className="pp-import__header">
        <h1 className="pp-import__title">Imports</h1>
        <Link to="/imports/new" className="pp-button" data-variant="primary">
          Import a spreadsheet
        </Link>
      </header>
      {imports.length === 0 ? (
        <Surface>
          <div>
            <h2 className="pp-import__heading">No imports yet</h2>
            <p>
              Bring a catalogue over from a spreadsheet. We check every GTIN, show you the
              rows we can&apos;t accept, and create the rest as drafts.
            </p>
          </div>
        </Surface>
      ) : (
        <table className="pp-import__history">
          <thead>
            <tr>
              <th scope="col">File</th>
              <th scope="col">Rows</th>
              <th scope="col">Where it is</th>
              <th scope="col">Started</th>
            </tr>
          </thead>
          <tbody>
            {imports.map((row) => {
              const rows = readStats(row.stats).rows;
              return (
                <tr key={row.id} data-import-id={row.id}>
                  <td>
                    <Link to="/imports/$importId" params={{ importId: row.id }}>
                      {row.filename}
                    </Link>
                  </td>
                  <td>{rows === undefined ? "—" : count(rows)}</td>
                  <td>{STATUS_WORDS[row.status]}</td>
                  <td>{row.createdAt.slice(0, 10)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
